import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";

const ArtistFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  initialData = null,
}) => {
  const [form, setForm] = useState({
    stage_name: "",
    real_name: "",
    phone: "",
    email: "",
    dob: "",
    closestAirports: [],
    airportInput: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    county: "",
    country: "",
    postcode: "",
    next_of_kin_name: "",
    next_of_kin_phone: "",
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        stage_name: initialData.stage_name || "",
        real_name: initialData.real_name || "",
        phone: initialData.phone || "",
        email: initialData.email || "",
        dob: initialData.dob || "",
        closestAirports: initialData.closest_airports?.map((a) => a.name) || [],
        airportInput: "",
        addressLine1: initialData.address?.address_1 || "",
        addressLine2: initialData.address?.address_2 || "",
        addressLine3: initialData.address?.address_3 || "",
        city: initialData.address?.city || "",
        county: initialData.address?.county || "",
        country: initialData.address?.country || "",
        postcode: initialData.address?.postcode || "",
        next_of_kin_name: initialData.next_of_kin_name || "",
        next_of_kin_phone: initialData.next_of_kin_phone || "",
      });
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  const handleAirportKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const value = form.airportInput.trim();
      if (!value) return;

      if (!form.closestAirports.includes(value)) {
        setForm((prev) => ({
          ...prev,
          closestAirports: [...prev.closestAirports, value],
          airportInput: "",
        }));
      }
    }
  };

  const removeAirport = (name) => {
    setForm((prev) => ({
      ...prev,
      closestAirports: prev.closestAirports.filter((a) => a !== name),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            Edit Artist Details
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Basic Info */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Stage Name
              </label>
              <input
                type="text"
                value={form.stage_name}
                onChange={(e) => handleChange("stage_name", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Real Name
              </label>
              <input
                type="text"
                value={form.real_name}
                onChange={(e) => handleChange("real_name", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Phone No:
              </label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Email Address
              </label>
              <input
                type="text"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Next of Kin Name:
              </label>
              <input
                type="text"
                value={form.next_of_kin_name}
                onChange={(e) =>
                  handleChange("next_of_kin_name", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Next of Kin Phone:
              </label>
              <input
                type="text"
                value={form.next_of_kin_phone}
                onChange={(e) =>
                  handleChange("next_of_kin_phone", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Date of Birth:
              </label>
              <input
                type="date"
                value={form.dob}
                onChange={(e) => handleChange("dob", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Closest Airports
              </label>

              {/* Input */}
              <input
                type="text"
                placeholder="Type airport name and press Enter"
                value={form.airportInput}
                onChange={(e) => handleChange("airportInput", e.target.value)}
                onKeyDown={handleAirportKeyDown}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-3">
                {form.closestAirports.map((airport) => (
                  <span
                    key={airport}
                    className="flex items-center gap-2 bg-slate-800 text-slate-200 px-3 py-1 rounded-full text-xs"
                  >
                    {airport}
                    <button
                      type="button"
                      onClick={() => removeAirport(airport)}
                      className="text-slate-400 hover:text-white"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">Address</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                placeholder="Address Line 1"
                value={form.addressLine1}
                onChange={(e) => handleChange("addressLine1", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 2"
                value={form.addressLine2}
                onChange={(e) => handleChange("addressLine2", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 3"
                value={form.addressLine3}
                onChange={(e) => handleChange("addressLine3", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="City"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="County"
                value={form.county}
                onChange={(e) => handleChange("county", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Country"
                value={form.country}
                onChange={(e) => handleChange("country", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Postcode"
                value={form.postcode}
                onChange={(e) => handleChange("postcode", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition
              ${
                loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }
            `}
          >
            {loading ? "Saving..." : "Edit Artist"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtistFormModal;
